import { CONNECTION_COLOR_PRESETS } from './connectionColorPresets';

export type ConnectionColorAccentProps = {
  /** `connection.color` — a raw preset hex (e.g. `#EF4444`) or undefined. */
  color: string | undefined;
  /**
   * Compact cards (StartPage's disconnected cluster, `ConnectionCardCompact`
   * with `compact`) have less corner headroom than the full Connections
   * Dialog card — shrink the marker and its inset to match.
   */
  compact?: boolean;
};

/**
 * Connection-color treatment shared by `ConnectionCard` and
 * `ConnectionCardCompact`.
 *
 * U14 — replaces the old `borderLeftWidth: '4px'` bar. That bar followed the
 * card's `rounded-lg`/`rounded-md` curve and tapered into an awkward sliver
 * at the corner — the "AI vibe coded" look the user flagged. The
 * replacement pairs two elements that do different jobs, per the approved
 * design:
 *  - a faint, ALPHA-DERIVED wash across the whole card (ambient — lets you
 *    scan a long list and group at a glance without any element shouting).
 *    `color-mix(in srgb, <hex> var(--connection-wash-mix), transparent)`
 *    derives the alpha at render time so the raw, fully-saturated preset hex
 *    (`#EF4444`, ...) never paints directly — a raw hex behaves completely
 *    differently against a dark card than a light one, an alpha-mixed one
 *    blends with whatever's underneath in both.
 *
 *    U14 FOLLOW-UP — the mix percentage itself is theme-aware
 *    (`--connection-wash-mix`, `src/index.css`: 7% light / 16% dark). A
 *    single 7% shipped for both themes at first; measured (OKLab
 *    delta-chroma of the blended card vs. the plain panel, both themes,
 *    across all 18 presets) and confirmed by re-capturing the Connections
 *    Dialog, it read fine on light but went visually flat on dark — dark's
 *    `--panel` already carries a small built-in navy chroma that a
 *    differently-hued 7% wash barely overcomes (worst case, Amber, made the
 *    card read LESS colorful than the plain no-color card). 16% restores a
 *    comparable margin. See `src/index.css`'s `--connection-wash-mix`
 *    comment and `task-U14-report.md` for the full measured numbers — do
 *    not hardcode a percentage here again; it must stay a CSS var so both
 *    themes can be tuned independently in one place.
 *  - a small, fully-saturated triangle in the top-left corner (precise —
 *    the Excel cell-comment corner-marker convention: it tells you exactly
 *    which color this is, which the wash alone cannot at any opacity subtle
 *    enough to be tasteful).
 *
 * Radius decision (REVISED — the marker is now FLUSH to the corner): the
 * triangle sits at `top-0 left-0` inside a dedicated clip layer, so it fills
 * right up to the card's edge and the card's own curve carves the outer
 * corner off it. It shipped inset at `top-2 left-2` first; read against the
 * card's edge that inset looked like a mistake rather than a choice, so the
 * marker was moved flush.
 *
 * The clip layer is why this is safe. `overflow-hidden` cannot go on the
 * CARD root: `ConnectionCardCompact` conditionally applies `shadow-sm` to
 * that same element for active/focused states, and `overflow-hidden` on a
 * box also clips that box's own outer `box-shadow` — putting it there would
 * silently kill the shadow. So the clipping lives on an inert
 * `absolute inset-0` child that has no shadow of its own to lose. That child
 * takes `rounded-[inherit]`, so one clip layer serves both callers' radii
 * (`rounded-lg` 8px on `ConnectionCard`, `rounded-md` 6px on
 * `ConnectionCardCompact`) without either being named here.
 *
 * `inset-0` resolves against the padding box, i.e. inside the 1px border —
 * so the marker meets the border rather than painting over it.
 *
 * Sizes are a little larger than the inset version's (14px/10px) because the
 * corner curve now eats the wedge's outermost corner; the *visible* area is
 * about what it was before.
 *
 * Stacking contract: the wash must paint BEHIND the header content (icon,
 * name), so it uses `-z-10`. The triangle is a foreground marker (like
 * Excel's, it sits ON TOP of whatever's in the corner) and uses ordinary
 * DOM order — no z-index needed, since a normal absolutely-positioned
 * element already paints after non-positioned in-flow siblings. For the
 * wash's `-z-10` to stay confined to this card (instead of escaping to
 * paint behind a distant ancestor's background), the caller's root element
 * must have `relative isolate` — `isolate` opens a stacking context without
 * requiring an explicit `z-index` on the root itself.
 *
 * Accessibility: color is never the only signal — every marker carries a
 * `title`/`aria-label` naming the preset (falls back to the raw hex for a
 * custom, non-preset color).
 */
export function ConnectionColorAccent({ color, compact = false }: ConnectionColorAccentProps) {
  if (!color) return null;

  const preset = CONNECTION_COLOR_PRESETS.find(
    (candidate) => candidate.hex.toLowerCase() === color.toLowerCase()
  );
  const accessibleName = preset ? `Connection color: ${preset.label}` : `Connection color: ${color}`;

  const triangleSize = compact ? 'h-3 w-3' : 'h-4 w-4';

  return (
    <>
      <div
        aria-hidden="true"
        data-testid="connection-color-wash"
        className="pointer-events-none absolute inset-0 -z-10 rounded-[inherit]"
        style={{ background: `color-mix(in srgb, ${color} var(--connection-wash-mix), transparent)` }}
      />
      <div
        aria-hidden="true"
        data-testid="connection-color-marker-clip"
        className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
      >
        <span
          role="img"
          aria-label={accessibleName}
          title={accessibleName}
          data-testid="connection-color-marker"
          className={`absolute top-0 left-0 ${triangleSize}`}
          style={{ backgroundColor: color, clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
        />
      </div>
    </>
  );
}
